/**
 * `AIAgentToolRegistryModel.ts` — port of Unity's
 * `AI/Registry/Core_Registry.cs` (metadata shape + schema helpers + default
 * tool assembly). Domain tool lists live in sibling files mirroring the
 * Unity partial split (`Entities_Registry.ts`, `Scenes_Registry.ts`, …).
 *
 * Web slice note: only tools with working web executors are registered.
 * Unity domains not yet ported (annotations, PDF, playback/wait routing,
 * Spark avatar, recording) stay out of the registry so the agent never
 * sees a tool that would dead-end in `tool_not_supported`.
 *
 * The named exception is the Spark Preview stubs — see
 * `executor/previewNotImplemented.ts`. Those answer with a spoken "not built
 * yet" rather than a dead end, because the questions they cover (recall of a
 * past conversation, opening a document on the passage that answered) are ones
 * the agent will otherwise answer from imagination.
 */

import { addEntitiesTools } from "./Entities_Registry";
import { addScenesTools } from "./Scenes_Registry";
import { addAssetsTools } from "./Assets_Registry";
import { addSelectionSetsTools } from "./SelectionSets_Registry";
import { addProjectTools } from "./Project_Registry";
import { addContextTools } from "./Context_Registry";
import { addAIBehaviourTools, buildSwitchRoleDescription } from "./AIBehaviour_Registry";
import { addWorkflowTools } from "./Workflow_Registry";
import { addDocsTools } from "./Docs_Registry";
import { addMemoryTools } from "./Memory_Registry";
import { AgentToolNames } from "./agentToolNames";
import { setScriptCommandCatalog } from "./scriptCommandCatalog";
import { getRoleIds } from "@ai/guardrails/agentPrompts";

export type JsonSchema = Record<string, unknown>;

/** Port of Unity's `AIAgentTool`. */
export interface AIAgentTool {
  name: string;
  /** Human-facing grouping used by the tool browser UI. */
  category: string;
  description: string;
  /** Optional short human-facing tooltip text. */
  humanDescription?: string;
  parameters: JsonSchema;
  enabled: boolean;
  writerOnly: boolean;
  /** Optional label shown in the AI panel while this tool is executing. */
  labelWhenActive?: string;
}

/** `AIAgentTool.ToRealtimeTool()` — the shape sent in `session.update`. */
export function toRealtimeTool(tool: AIAgentTool): Record<string, unknown> {
  return {
    type: "function",
    name: tool.name,
    description: tool.description,
    parameters: tool.parameters,
  };
}

// ---------------------------------------------------------------------------
// Schema helpers (ports of the private static helpers in Core_Registry.cs).
// `__required` is stripped by `buildParams` and folded into `required[]`.
// ---------------------------------------------------------------------------

type ParamSpec = Record<string, unknown> & { __required?: boolean };

function formatDesc(description: string, optional: boolean): string {
  if (!description) return description;
  if (optional && !/^optional/i.test(description)) return `Optional. ${description}`;
  return description;
}

export function stringParam(description: string, optional = true): ParamSpec {
  return { type: "string", description: formatDesc(description, optional), __required: !optional };
}

export function boolParam(
  description: string,
  defaultBool?: boolean,
  optional = true,
): ParamSpec {
  const res: ParamSpec = {
    type: "boolean",
    description: formatDesc(description, optional),
    __required: !optional,
  };
  if (defaultBool !== undefined) res.default = defaultBool;
  return res;
}

export function numberParam(description: string, optional = true): ParamSpec {
  return { type: "number", description: formatDesc(description, optional), __required: !optional };
}

export function numberList(description: string, optional = true): ParamSpec {
  return {
    type: "array",
    items: { type: "number" },
    description: formatDesc(description, optional),
    __required: !optional,
  };
}

export function stringList(description: string, optional = true): ParamSpec {
  return {
    type: "array",
    items: { type: "string" },
    description: formatDesc(description, optional),
    __required: !optional,
  };
}

export function stringEnum(
  description: string,
  optional: boolean,
  ...values: string[]
): ParamSpec {
  return {
    type: "string",
    description: formatDesc(description, optional),
    enum: values,
    __required: !optional,
  };
}

export function buildParams(
  properties?: Record<string, ParamSpec | Record<string, unknown>>,
  ...required: string[]
): JsonSchema {
  const props: Record<string, Record<string, unknown>> = {};
  const reqList = [...required];
  for (const [key, spec] of Object.entries(properties ?? {})) {
    const clone: Record<string, unknown> = { ...spec };
    if (clone.__required === true && !reqList.includes(key)) reqList.push(key);
    delete clone.__required;
    props[key] = clone;
  }
  return { type: "object", properties: props, required: reqList };
}

// ---------------------------------------------------------------------------
// Registry model
// ---------------------------------------------------------------------------

export class AIAgentToolRegistryModel {
  private readonly m_tools: AIAgentTool[];

  constructor() {
    this.m_tools = createDefaultTools();
    setScriptCommandCatalog(this.m_tools);
  }

  get allTools(): ReadonlyArray<AIAgentTool> {
    return this.m_tools;
  }

  setEnabled(name: string, enabled: boolean): void {
    const tool = this.m_tools.find((t) => t.name === name);
    if (tool) tool.enabled = enabled;
  }

  /** Returns the `labelWhenActive` for the tool, or `null` if not set. */
  getLabelWhenActive(toolName: string): string | null {
    const tool = this.m_tools.find((t) => t.name === toolName);
    return tool?.labelWhenActive || null;
  }

  /**
   * Port of Unity `RefreshAIBehaviourDynamicSchema`: keeps the `switch_role` role enum in sync
   * with the roles loaded from `/AI/roles/*.md`, so roles added at runtime become
   * model-switchable without a code change. Safe to call on every schema emit.
   */
  refreshAIBehaviourDynamicSchema(): void {
    const switchRole = this.m_tools.find((t) => t.name === AgentToolNames.SwitchRole);
    if (!switchRole) return;

    // The PROSE is recomposed too, not just the enum: the role descriptions come
    // from `/AI/roles/*.md`, which load after registration, so a tool left with
    // its registration-time text would list the legacy bootstrap ids with no
    // descriptions for the life of the session.
    switchRole.description = buildSwitchRoleDescription();

    const properties = switchRole.parameters?.["properties"];
    if (!properties || typeof properties !== "object") return;

    const roleParam = (properties as Record<string, unknown>)["role"];
    if (!roleParam || typeof roleParam !== "object") return;

    (roleParam as Record<string, unknown>)["enum"] = [...getRoleIds()];
  }

  /**
   * @param allowedToolIds When non-null, only tools whose wire id is in the set (compared
   * lowercase) are emitted — the role/skill gate. Null = no restriction (Unity parity).
   */
  getEnabledToolSchemas(
    allowedToolIds?: ReadonlySet<string> | null,
  ): ReadonlyArray<Record<string, unknown>> {
    return this.m_tools
      .filter(
        (t) =>
          t.enabled && (allowedToolIds == null || allowedToolIds.has(t.name.toLowerCase())),
      )
      .map(toRealtimeTool);
  }
}

function createDefaultTools(): AIAgentTool[] {
  const tools: AIAgentTool[] = [];
  addEntitiesTools(tools);
  addScenesTools(tools);
  addAssetsTools(tools);
  addSelectionSetsTools(tools);
  addProjectTools(tools);
  addContextTools(tools);
  addAIBehaviourTools(tools);
  addWorkflowTools(tools);
  addDocsTools(tools);
  addMemoryTools(tools);
  // `create_md_file` (Unity `Documents_Registry`) deliberately not wired: it is
  // `Enabled = false` upstream too, and it AUTHORS a file rather than reading
  // the library — unrelated to document retrieval.
  return tools;
}

setScriptCommandCatalog(createDefaultTools());

// ---------------------------------------------------------------------------
// Tools facade — port of Unity's `AIAgentToolsModel` (`IAIAgentToolsModel`).
// ---------------------------------------------------------------------------

export interface IToolExecutor {
  executeAsync(toolName: string, args: Record<string, unknown>, triggeredBy?: string): Promise<string>;
}

/**
 * Who ran a tool. Rides every `executeAsync` and both execution events, and is
 * read by three unrelated consumers, which is why the vocabulary lives here
 * rather than as a literal at each call site: the debug feed attributes a card
 * by it, observability records it, and — since the workflow-playback fix —
 * `IAIGuardrailsModel.authorizeToolExecution` decides the writer gate on it. A
 * caller and the guardrail disagreeing about the spelling would silently
 * restore the bug that gate change exists to fix, with nothing failing to
 * compile.
 *
 * It lives beside {@link IToolExecutor} because that interface declares the
 * parameter; guardrails and the executor both already import from this module,
 * so neither needs a new dependency edge to reach it.
 */
export const TOOL_TRIGGER = {
  /** The realtime agent, or the Responses text bridge acting as it. */
  AI: "AI",
  /**
   * A workflow script step, played back by `WorkflowScriptRunner`.
   *
   * ⚠ Load-bearing: this value is what exempts an authored script command from
   * the `writerOnly` role gate, so a learner with no editor access can be
   * guided. Never reuse it for something the agent decides to do.
   */
  WORKFLOW: "workflow",
} as const;

export type ToolTrigger = (typeof TOOL_TRIGGER)[keyof typeof TOOL_TRIGGER];

/**
 * Tools enabled even when no project is open (`s_toolsEnabledWithoutProject`).
 *
 * Exported so the AI debug panel can say WHICH half of the availability gate
 * withheld a tool. Reading `enabled` alone only says a tool is off; the two sets
 * below are the difference between "no project is open" and "never available to
 * an agent", and a panel that exists to answer "why can't it do X" has to tell
 * them apart. Read-only — the gate itself stays `refreshToolAvailability`'s.
 */
export const TOOLS_ENABLED_WITHOUT_PROJECT: ReadonlySet<string> = new Set([
  AgentToolNames.GetAvailableProjects,
  AgentToolNames.OpenProject,
  AgentToolNames.CreateNewProject,
  AgentToolNames.UseConstantListening,
  AgentToolNames.ChangeWakeGesture,
  AgentToolNames.ToggleGuide,
  AgentToolNames.ListenUser,
  AgentToolNames.SendUserText,
  AgentToolNames.ChangePersonality,
  AgentToolNames.ResetConversation,
  AgentToolNames.ToggleAIPanel,
  AgentToolNames.ToggleSpark,
  AgentToolNames.ToggleDebugMode,
]);

/** Tools kept away from agents entirely (`s_toolsDisabledForAgents`). See above. */
export const TOOLS_DISABLED_FOR_AGENTS: ReadonlySet<string> = new Set([
  AgentToolNames.UpdateMaterialProperties,
]);

export interface IAIAgentToolsModel {
  readonly toolExecutorModel: IToolExecutor;
  readonly allTools: ReadonlyArray<AIAgentTool>;
  getEnabledToolSchemas(): ReadonlyArray<Record<string, unknown>>;
  getLabelWhenActive(toolName: string): string | null;
  refreshToolAvailability(hasProject: boolean): void;
  /**
   * Registers the role/skill tool gate (Unity `SetRoleSkillToolFilterProvider`). The provider is
   * invoked on every schema emit; null result = no restriction.
   */
  setRoleSkillToolFilterProvider(provider: () => ReadonlySet<string> | null): void;
}

export class AIAgentToolsModel implements IAIAgentToolsModel {
  private readonly m_toolRegistryModel: AIAgentToolRegistryModel;
  readonly toolExecutorModel: IToolExecutor;
  private m_roleSkillToolFilterProvider: (() => ReadonlySet<string> | null) | null = null;

  constructor(toolRegistryModel: AIAgentToolRegistryModel, toolExecutorModel: IToolExecutor) {
    this.m_toolRegistryModel = toolRegistryModel;
    this.toolExecutorModel = toolExecutorModel;
  }

  get allTools(): ReadonlyArray<AIAgentTool> {
    return this.m_toolRegistryModel.allTools;
  }

  setRoleSkillToolFilterProvider(provider: () => ReadonlySet<string> | null): void {
    this.m_roleSkillToolFilterProvider = provider;
  }

  getEnabledToolSchemas(): ReadonlyArray<Record<string, unknown>> {
    // Keep runtime-driven schema fields (e.g. the switch_role role list) current before emitting.
    this.m_toolRegistryModel.refreshAIBehaviourDynamicSchema();
    const allowed = this.m_roleSkillToolFilterProvider?.() ?? null;
    return this.m_toolRegistryModel.getEnabledToolSchemas(allowed);
  }

  getLabelWhenActive(toolName: string): string | null {
    return this.m_toolRegistryModel.getLabelWhenActive(toolName);
  }

  refreshToolAvailability(hasProject: boolean): void {
    for (const tool of this.m_toolRegistryModel.allTools) {
      tool.enabled =
        !TOOLS_DISABLED_FOR_AGENTS.has(tool.name) &&
        (hasProject || TOOLS_ENABLED_WITHOUT_PROJECT.has(tool.name));
    }
  }
}

export const AI_AGENT_TOOLS_MODEL = Symbol.for("Campfire.AIAgentToolsModel");
